/**
 * W-090 — Admin users. Stories: US-A01 AC5.
 * Create, edit, deactivate admin accounts and assign roles. Super Admin only —
 * the access matrix gives `admin_users` to `super_admin` alone, and the RLS
 * policy on the table does the same (docs/03 §4.3).
 */
import { List, useModalForm, useTable } from '@refinedev/antd'
import { useInvalidate } from '@refinedev/core'
import type { HttpError } from '@refinedev/core'
import { Alert, Button, Form, Input, Modal, Select, Space, Switch, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import { recordAudit } from '@/lib/audit'
import { invokeEdgeFunction } from '@/lib/supabase'
import { ModelInsights } from '@/components'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { AdminRole, AdminUser } from '@/types'
import { ADMIN_ROLES } from '@/types'

const ROLE_KEYS: Record<AdminRole, string> = {
  super_admin: 'admin.roles.superAdmin',
  content_manager: 'admin.roles.contentManager',
  moderator: 'admin.roles.moderator',
  support: 'admin.roles.support',
}

type AdminUserEditValues = Pick<AdminUser, 'role' | 'is_active'>

interface InviteValues {
  email: string
  full_name: string
  role: AdminRole
}

export const AdminUserListPage = () => {
  const translate = useAdminTranslate()
  const { tableProps } = useTable<AdminUser>({
    // TODO(US-A01): `admin_users` holds only id/role/is_active; the e-mail and
    // the name live in auth.users, which PostgREST does not expose. The backend
    // agent owns an `admin_users_view` joining the two — working name.
    resource: 'admin_users_view',
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 25 },
  })
  const invalidate = useInvalidate()
  const roleLabel = (value: AdminRole) => translate(ROLE_KEYS[value])
  const roleOptions = ADMIN_ROLES.map(value => ({ value, label: roleLabel(value) }))

  /** The edit form only exposes the two mutable columns of `admin_users`. */
  const editModal = useModalForm<AdminUser, HttpError, AdminUserEditValues>({
    resource: 'admin_users',
    action: 'edit',
    redirect: false,
  })

  const inviteModal = useModalForm<AdminUser, HttpError, InviteValues>({
    resource: 'admin_users',
    action: 'create',
    redirect: false,
  })

  const refresh = () => void invalidate({ resource: 'admin_users_view', invalidates: ['list'] })

  return (
    <List
      title={translate('admin.pages.administrators')}
      headerButtons={
        <Button type="primary" onClick={() => inviteModal.show()}>
          {translate('admin.screens.adminUsers.invite')}
        </Button>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <ModelInsights model="administrators" />
        <Alert
          type="warning"
          showIcon
          message={translate('admin.screens.adminUsers.restrictedTitle')}
          description={translate('admin.screens.adminUsers.restrictedBody')}
        />

        <Table<AdminUser>
          {...tableProps}
          rowKey="id"
          columns={[
            {
              title: translate('admin.columns.name'),
              dataIndex: 'full_name',
              render: (v: string | null) => v ?? '—',
            },
            {
              title: translate('admin.columns.email'),
              dataIndex: 'email',
              render: (v: string | null) => v ?? '—',
            },
            {
              title: translate('admin.columns.role'),
              dataIndex: 'role',
              render: (value: AdminRole) => <Tag>{roleLabel(value)}</Tag>,
            },
            {
              title: translate('admin.common.activeMasc'),
              dataIndex: 'is_active',
              render: (value: boolean) =>
                value ? (
                  <Tag color="success">{translate('admin.common.active')}</Tag>
                ) : (
                  <Tag color="error">{translate('admin.common.deactivated')}</Tag>
                ),
            },
            {
              title: translate('admin.columns.createdAt'),
              dataIndex: 'created_at',
              render: (value: string) => dayjs(value).format('DD/MM/YYYY'),
            },
            {
              title: translate('admin.columns.actions'),
              dataIndex: 'id',
              render: (_: unknown, record: AdminUser) => (
                <Button size="small" onClick={() => editModal.show(record.id)}>
                  {translate('admin.common.edit')}
                </Button>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        {...editModal.modalProps}
        title={translate('admin.screens.adminUsers.editTitle')}
        forceRender
      >
        <Form
          {...editModal.formProps}
          layout="vertical"
          onFinish={async values => {
            await editModal.onFinish?.(values)
            await recordAudit({
              action: 'admin_user.role_change',
              target: 'admin_users',
              targetId: String(editModal.id ?? ''),
              after: values,
            })
            refresh()
          }}
        >
          <Form.Item
            name="role"
            label={translate('admin.columns.role')}
            rules={[{ required: true }]}
          >
            <Select options={roleOptions} />
          </Form.Item>
          <Form.Item
            name="is_active"
            label={translate('admin.common.activeMasc')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        {...inviteModal.modalProps}
        title={translate('admin.screens.adminUsers.invite')}
        forceRender
        onOk={async () => {
          const values = await inviteModal.form.validateFields()
          // Creating an admin means creating an auth user, which needs the
          // service role. `admin-user-invite` sends the invitation e-mail and
          // inserts the admin_users row in one call, and writes the audit entry
          // itself — so no recordAudit() here (see lib/audit.ts: actions with
          // their own edge function write their own audit row).
          await invokeEdgeFunction('admin-user-invite', { ...values })
          inviteModal.close()
          refresh()
        }}
      >
        <Form {...inviteModal.formProps} layout="vertical">
          <Form.Item
            name="email"
            label={translate('admin.screens.adminUsers.emailLabel')}
            rules={[{ required: true, type: 'email' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="full_name"
            label={translate('admin.screens.adminUsers.fullNameLabel')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label={translate('admin.columns.role')}
            rules={[{ required: true }]}
          >
            <Select options={roleOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </List>
  )
}
